import { Protocol } from "game-common/protocol"
import type { TutorialType } from "game-common/models"
import type { Client } from "../client"
import type { TutorialUse } from "../rendering/impl/pixijs/gui/tutorial_demo"

/**
 * Where a player is putting their attention, for session replays: every click or tap, at the spot on
 * their screen it landed, and the size of that screen.
 *
 * The live half (`installAttentionTelemetry`) sends each one to the server as type_clientTelemetry.
 * The server does nothing with it -- the session recording keeps everything a client sends, and that
 * is the whole point (server/src/admin/session_recordings.ts). The replay half (`AttentionOverlay`)
 * draws a ripple where the click landed, at the moment it did.
 *
 * Positions are CSS pixels in the player's window, alongside the window's size. The /admin viewer
 * gives the replay a window of exactly that size (scaled down to fit), so the game lays out as it did
 * for the player and a click on a button lands on that button in the replay too.
 */

export type AttentionTelemetry =
    | { kind: "viewport"; w: number; h: number }
    | { kind: "click"; x: number; y: number; button: number; pointer: string }

/**
 * The other thing sent only to be recorded: what the player did to a tutorial on their own screen
 * (gui/tutorial.ts), which a replay acts out (Client.actOutRecordedAnswer).
 */
export type ClientTelemetry = AttentionTelemetry | ({ kind: "tutorial"; tutorial: TutorialType } & TutorialUse)

/** A click storm (a held autoclicker, a frantic tap) records at most this often. */
const MIN_CLICK_GAP_MS = 50
/** How often to check for a session that has not been told the window size yet. */
const VIEWPORT_CHECK_MS = 500

/** The live player's side. Not installed for a replay, which has no player of its own. */
export const installAttentionTelemetry = (client: Client) => {
    let sentTo: unknown
    let sentW = 0
    let sentH = 0
    let lastClickTs = 0

    const sendViewport = () => {
        if (!client.clientGameLogic.playerEntity) return
        const w = Math.round(window.innerWidth)
        const h = Math.round(window.innerHeight)
        // Once per connection (a redial is a new session, a new recording), and on every change.
        if (sentTo === client.ws && w === sentW && h === sentH) return
        if (!client.sendTelemetry({ kind: "viewport", w, h })) return
        sentTo = client.ws
        sentW = w
        sentH = h
    }

    window.addEventListener(
        "pointerdown",
        event => {
            const now = Date.now()
            if (now - lastClickTs < MIN_CLICK_GAP_MS || !client.clientGameLogic.playerEntity) return
            lastClickTs = now
            sendViewport()
            client.sendTelemetry({
                kind: "click",
                x: Math.round(event.clientX),
                y: Math.round(event.clientY),
                button: event.button,
                pointer: event.pointerType || "mouse",
            })
        },
        { capture: true, passive: true },
    )
    window.addEventListener("samsara:canvas-resize", sendViewport)
    window.addEventListener("resize", sendViewport)
    setInterval(sendViewport, VIEWPORT_CHECK_MS)
}

/** Reads a recorded client frame; undefined unless it is attention telemetry. */
export const parseAttention = (text: string): AttentionTelemetry | undefined => {
    if (!text.includes(`"payloadType":"${Protocol.type_clientTelemetry}"`)) return undefined
    try {
        const payload = JSON.parse(text)?.payload
        if (payload?.kind === "viewport" && payload.w > 0 && payload.h > 0) return payload
        if (payload?.kind === "click" && Number.isFinite(payload.x) && Number.isFinite(payload.y)) return payload
    } catch {
        // A frame cut off mid-write; nothing to show.
    }
    return undefined
}

/**
 * Every click in one colour, picked to stand out against the game: cyan appears nowhere in the ember
 * palette (fire, earth, night blue). A dark rim keeps it readable over snow and bright UI too. The
 * button shows in the ring instead: solid for a left click or a tap, dashed for a right click.
 */
const CLICK_COLOR = "#00e5ff"
const CLICK_LIFETIME_MS = 1500

const RIPPLE_STYLE = `
.replay-attention { position: fixed; inset: 0; pointer-events: none; z-index: 2147483000; }
.replay-click { position: absolute; width: 0; height: 0; }
.replay-click .ring, .replay-click .dot { position: absolute; border-radius: 50%; }
.replay-click .ring {
    left: -14px; top: -14px; width: 28px; height: 28px; box-sizing: border-box;
    border: 2px solid ${CLICK_COLOR};
    box-shadow: 0 0 0 1px rgba(0, 0, 0, .55), 0 0 6px 1px ${CLICK_COLOR};
    animation: replay-ripple 700ms ease-out forwards;
}
.replay-click.secondary .ring { border-style: dashed; }
.replay-click .dot {
    left: -4px; top: -4px; width: 8px; height: 8px;
    background: ${CLICK_COLOR};
    box-shadow: 0 0 0 1px rgba(0, 0, 0, .6), 0 0 5px 1px ${CLICK_COLOR};
    animation: replay-dot ${CLICK_LIFETIME_MS}ms ease-in forwards;
}
@keyframes replay-ripple {
    0% { transform: scale(.3); opacity: .9 }
    100% { transform: scale(1.3); opacity: 0 }
}
@keyframes replay-dot {
    0% { opacity: 1 }
    60% { opacity: .9 }
    100% { opacity: 0 }
}
`

/** The replay's side: ripples over the game where the recorded player clicked. */
export class AttentionOverlay {
    private layer?: HTMLDivElement
    /** The recorded player's window, once the recording has said. */
    viewport?: { w: number; h: number }

    show = (click: Extract<AttentionTelemetry, { kind: "click" }>) => {
        const layer = this.ensureLayer()
        // 1 when the frame is the player's size, which the admin viewer arranges.
        const scaleX = this.viewport ? window.innerWidth / this.viewport.w : 1
        const scaleY = this.viewport ? window.innerHeight / this.viewport.h : 1
        const marker = document.createElement("div")
        marker.className = click.pointer !== "touch" && click.button === 2 ? "replay-click secondary" : "replay-click"
        marker.style.left = `${click.x * scaleX}px`
        marker.style.top = `${click.y * scaleY}px`
        // A small ripple and a dot, gone by CLICK_LIFETIME_MS: enough to notice, not to distract.
        for (const part of ["ring", "dot"]) {
            const piece = document.createElement("div")
            piece.className = part
            marker.appendChild(piece)
        }
        layer.appendChild(marker)
        setTimeout(() => marker.remove(), CLICK_LIFETIME_MS + 100)
    }

    private ensureLayer = (): HTMLDivElement => {
        if (this.layer) return this.layer
        const style = document.createElement("style")
        style.textContent = RIPPLE_STYLE
        document.head.appendChild(style)
        const layer = document.createElement("div")
        layer.className = "replay-attention"
        document.body.appendChild(layer)
        this.layer = layer
        return layer
    }
}
